"use client";

import { useState } from "react";
import nextDynamic from "next/dynamic";
import Link from "next/link";
import { BellPlus } from "lucide-react";

import { useSession } from "@/lib/auth-client";
import { dpopApi } from "@/lib/dpop-client";
import { cn } from "@/lib/utils";
import { useLoginRedirectHref } from "@/hooks/use-login-redirect";
import { useDataProduct as useProduct } from "@/hooks/use-market-data";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import { SharePopover } from "@/components/shared/share-popover";

const DASH = "—";

const RunDiligenceButton = nextDynamic(
  () =>
    import("@/components/dashboard/interactions/research-progress").then(
      (mod) => ({ default: mod.RunDiligenceButton }),
    ),
  { ssr: false, loading: () => null },
);

// Existing terminal data views, adapted to the Strategies asset context.
function num(v: any): number | null {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function fmtPx(v: any): string {
  const n = num(v);
  if (n === null) return DASH;
  const abs = Math.abs(n);
  const dp = abs >= 1000 ? 2 : abs >= 1 ? 3 : abs >= 0.01 ? 5 : 7;
  return n.toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
}

function fmtCompact(v: any): string {
  const n = num(v);
  if (n === null) return DASH;
  const a = Math.abs(n);
  const s = n < 0 ? "-" : "";
  if (a >= 1e9) return `${s}${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}${(a / 1e3).toFixed(1)}K`;
  return `${s}${a.toFixed(0)}`;
}

function fmtPct(v: any, dp = 2): string {
  const n = num(v);
  if (n === null) return DASH;
  return `${n >= 0 ? "+" : ""}${n.toFixed(dp)}%`;
}

function fmtAge(iso?: string | null): string {
  if (!iso) return DASH;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return DASH;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function fmtSecs(s: number | null): string {
  if (s === null || !Number.isFinite(s)) return DASH;
  if (s < 60) return `${Math.floor(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}

function signClass(v: any): string {
  const n = num(v);
  if (n === null) return "text-muted-foreground";
  if (n > 0) return "text-success";
  if (n < 0) return "text-danger";
  return "text-foreground";
}

function shortAddr(a?: string | null): string {
  if (!a) return DASH;
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="flex h-full items-center justify-center px-3 py-6 text-center text-xs text-muted-foreground/60">
      {msg}
    </div>
  );
}

function Gate({ error }: { error: string }) {
  const isSeat = /fexr weekly|fexr yearly|pay per call|402|payment/i.test(
    error,
  );
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-6 text-center">
      <div className="text-xs uppercase tracking-widest text-warning">
        {isSeat ? "Plan required" : "Unavailable"}
      </div>
      <div className="max-w-[17.5rem] text-xs leading-relaxed text-muted-foreground">
        {error}
      </div>
      {isSeat ? (
        <a
          href="/dashboard/billing"
          className="mt-1 border border-warning/40 px-2 py-1 text-xs text-warning transition-colors hover:bg-warning/10"
        >
          See plans →
        </a>
      ) : null}
    </div>
  );
}

function Prov({ p }: { p: any }) {
  if (!p || (!p.ts && p.age_seconds == null && !p.source_status)) return null;
  return (
    <div className="flex shrink-0 items-center gap-3 border-t border-border/40 px-2 py-1 text-xs text-muted-foreground/60">
      {p.age_seconds != null ? (
        <span className="font-number tabular-nums">
          AGE {fmtSecs(p.age_seconds)}
        </span>
      ) : null}
      {p.fresh != null ? (
        <span className={p.fresh ? "text-success/70" : "text-warning/80"}>
          {p.fresh ? "FRESH" : "STALE"}
        </span>
      ) : null}
      {p.source_status && p.source_status !== "ok" ? (
        <span className="text-warning/80">SRC {p.source_status}</span>
      ) : null}
      {p.methodology_url ? (
        <a
          href={p.methodology_url}
          target="_blank"
          rel="noreferrer"
          className="ml-auto underline decoration-dotted hover:text-foreground"
        >
          method
        </a>
      ) : null}
    </div>
  );
}

function FlowTab({ sym, nonce }: { sym: string; nonce: number }) {
  const whales = useProduct(
    sym
      ? `/v1/data/whale-alerts?asset=${encodeURIComponent(sym)}&limit=25&_=${nonce}`
      : null,
  );
  const smart = useProduct(
    sym
      ? `/v1/data/smart-money-flow?assets=${encodeURIComponent(sym)}&_=${nonce}`
      : null,
  );

  if (whales.error) return <Gate error={whales.error} />;
  if (whales.loading && !whales.value) return <Empty msg="loading flow…" />;

  const txs: any[] = whales.value?.transactions ?? [];
  const sm = smart.value?.assets?.[0];

  return (
    <div className="flex h-full min-h-0 flex-col">
      {sm ? (
        <div className="flex shrink-0 items-center gap-4 border-b border-border/40 px-2 py-1 text-xs">
          <span className="text-muted-foreground">
            7D{" "}
            <span className="font-number tabular-nums text-foreground">
              {fmtCompact(sm.large_transfer_activity_7d?.gross_usd)}
            </span>
            <span className="font-number tabular-nums text-muted-foreground/70">
              {" "}
              ({sm.large_transfer_activity_7d?.count ?? 0})
            </span>
          </span>
          <span className="text-muted-foreground">
            24H{" "}
            <span className="font-number tabular-nums text-foreground">
              {fmtCompact(sm.large_transfer_activity_24h?.gross_usd)}
            </span>
            <span className="font-number tabular-nums text-muted-foreground/70">
              {" "}
              ({sm.large_transfer_activity_24h?.count ?? 0})
            </span>
          </span>
          <span className="text-muted-foreground">
            MAX{" "}
            <span className="font-number tabular-nums text-foreground">
              {fmtCompact(sm.large_transfer_activity_7d?.largest_usd)}
            </span>
          </span>
          {/* labeled_share is the API's own honest attribution-coverage number. */}
          <span className="ml-auto font-number tabular-nums text-muted-foreground/70">
            LABELED{" "}
            {sm.labeled_share != null
              ? `${(sm.labeled_share * 100).toFixed(0)}%`
              : DASH}
          </span>
        </div>
      ) : null}

      <div className="custom-scrollbar overflow-auto">
        {txs.length ? (
          <>
            <div className="sticky top-0 z-10 flex border-b border-border/60 bg-background px-2 py-1 text-xs uppercase tracking-[0.08em] text-muted-foreground/60">
              <span className="w-10 shrink-0">Age</span>
              <span className="w-[3.625rem] shrink-0">Chain</span>
              <span className="w-[4.375rem] shrink-0 text-right">USD</span>
              <span className="w-[7.5rem] shrink-0 pl-2">From</span>
              <span className="w-[7.5rem] shrink-0">To</span>
            </div>
            {txs.map((t) => (
              <div
                key={t.tx_hash}
                style={{ minHeight: 32 }}
                className="flex items-center px-2 text-xs hover:bg-muted/30"
              >
                <span className="w-10 shrink-0 font-number tabular-nums text-muted-foreground">
                  {fmtAge(t.observed_at)}
                </span>
                <span className="w-[3.625rem] shrink-0 truncate text-muted-foreground">
                  {t.chain ?? DASH}
                </span>
                <span className="w-[4.375rem] shrink-0 text-right font-number tabular-nums text-foreground">
                  {fmtCompact(t.amount_usd)}
                </span>
                <span
                  className={cn(
                    "w-[7.5rem] shrink-0 truncate pl-2",
                    t.from_label ? "text-warning/90" : "text-muted-foreground",
                  )}
                  title={t.from ?? undefined}
                >
                  {t.from_label ?? shortAddr(t.from)}
                </span>
                <span
                  className={cn(
                    "w-[7.5rem] shrink-0 truncate",
                    t.to_label ? "text-warning/90" : "text-muted-foreground",
                  )}
                  title={t.to ?? undefined}
                >
                  {t.to_label ?? shortAddr(t.to)}
                </span>
              </div>
            ))}
          </>
        ) : (
          <Empty
            msg={whales.value?.coverage ?? `no tracked transfers for ${sym}`}
          />
        )}
      </div>
      {smart.error && (
        <p className="px-2 py-2 text-xs text-muted-foreground">
          Flow aggregates: {smart.error}
        </p>
      )}
      <Prov p={whales.provenance} />
    </div>
  );
}

function SupplyTab({ sym, nonce }: { sym: string; nonce: number }) {
  const q = useProduct(
    sym
      ? `/v1/data/token-unlocks?assets=${encodeURIComponent(sym)}&_=${nonce}`
      : null,
  );
  if (q.error) return <Gate error={q.error} />;
  if (q.loading && !q.value) return <Empty msg="loading supply…" />;
  const a = q.value?.assets?.[0];
  if (!a) return <Empty msg="no supply data" />;
  if (a.error) return <Empty msg={String(a.error)} />;

  const s = a.supply ?? {};
  const ru = a.remaining_unlock ?? {};
  const re = a.realized_emissions ?? {};
  const rs = a.real_schedule;

  const Row = ({ k, v, warn }: { k: string; v: string; warn?: boolean }) => (
    <div className="flex min-h-[1.875rem] items-center justify-between px-2 text-xs hover:bg-muted/20">
      <span className="text-muted-foreground">{k}</span>
      <span
        className={cn(
          "font-number tabular-nums",
          warn ? "text-warning" : "text-foreground",
        )}
      >
        {v}
      </span>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {a.data_quality ? (
        <div className="shrink-0 border-b border-warning/30 bg-warning/10 px-2 py-1 text-xs text-warning">
          DATA QUALITY: {a.data_quality} — derived figures suppressed
        </div>
      ) : null}
      <div className="custom-scrollbar overflow-auto">
        {rs ? (
          <>
            <div className="border-b border-border/40 bg-muted/20 px-2 py-0.5 text-xs uppercase tracking-widest text-success/80">
              Real schedule{rs.manually_verified ? " · verified" : ""}
            </div>
            <Row k="Next unlock" v={rs.next_unlock_date ?? DASH} />
            <Row k="Next unlock tokens" v={fmtCompact(rs.next_unlock_tokens)} />
            <Row
              k="Current rate / yr"
              v={fmtCompact(rs.as_of_rate_tokens_per_year)}
            />
            <Row k="Category" v={rs.category ?? DASH} />
          </>
        ) : null}

        <div className="border-y border-border/40 bg-muted/20 px-2 py-0.5 text-xs uppercase tracking-widest text-muted-foreground/70">
          Supply
        </div>
        <Row k="Circulating" v={fmtCompact(s.circulating)} />
        <Row k="Total" v={fmtCompact(s.total)} />
        <Row k="Max" v={fmtCompact(s.max)} />
        <Row
          k="Float %"
          v={s.float_pct != null ? `${s.float_pct.toFixed(2)}%` : DASH}
        />

        <div className="border-y border-border/40 bg-muted/20 px-2 py-0.5 text-xs uppercase tracking-widest text-muted-foreground/70">
          Non-circulating overhang (no timeline)
        </div>
        <Row k="Tokens" v={fmtCompact(ru.tokens)} />
        <Row k="USD at spot" v={fmtCompact(ru.usd)} />
        <Row
          k="vs ADV 30d"
          v={ru.vs_adv_30d != null ? `${ru.vs_adv_30d.toFixed(2)}×` : DASH}
          warn={num(ru.vs_adv_30d) !== null && num(ru.vs_adv_30d)! > 10}
        />
        {ru.cap_is_fallback ? (
          <Row k="Cap basis" v="total_supply (max missing)" warn />
        ) : null}

        <div className="border-y border-border/40 bg-muted/20 px-2 py-0.5 text-xs uppercase tracking-widest text-muted-foreground/70">
          Realised emissions
        </div>
        <Row k="Tokens / 30d" v={fmtCompact(re.tokens_per_30d)} />
        <Row
          k="Annualised inflation"
          v={
            re.annualized_inflation_pct != null
              ? `${re.annualized_inflation_pct.toFixed(2)}%`
              : DASH
          }
          warn={
            num(re.annualized_inflation_pct) !== null &&
            num(re.annualized_inflation_pct)! > 15
          }
        />
        <Row k="Daily emission USD" v={fmtCompact(re.daily_emission_usd)} />
        <Row
          k="Daily emission % ADV"
          v={
            re.daily_emission_pct_of_adv != null
              ? `${re.daily_emission_pct_of_adv.toFixed(2)}%`
              : DASH
          }
        />
        {a.impact_estimate?.full_overhang_pressure_bps != null ? (
          <Row
            k="Overhang impact"
            v={`${a.impact_estimate.full_overhang_pressure_bps} bps`}
          />
        ) : null}
      </div>
      <Prov p={q.provenance} />
    </div>
  );
}

function StatsTab({ sym, nonce }: { sym: string; nonce: number }) {
  const q = useProduct(
    sym
      ? `/v1/data/asset-metrics?asset=${encodeURIComponent(sym)}&_=${nonce}`
      : null,
  );
  if (q.error) return <Gate error={q.error} />;
  if (q.loading && !q.value) return <Empty msg="loading metrics…" />;
  const v = q.value;
  if (!v) return <Empty msg="no metrics" />;
  const r = v.returns_pct ?? {};
  const s = v.supply ?? {};

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="custom-scrollbar overflow-auto">
        <div className="grid grid-cols-4 divide-x divide-border/40 border-b border-border/40">
          {(["1h", "24h", "7d", "30d"] as const).map((k) => (
            <div key={k} className="flex flex-col px-2 py-1">
              <span className="text-xs uppercase text-muted-foreground/70">
                {k}
              </span>
              <span
                className={cn(
                  "font-number text-[0.75rem] tabular-nums",
                  signClass(r[k]),
                )}
              >
                {fmtPct(r[k])}
              </span>
            </div>
          ))}
        </div>
        {[
          ["Market cap", fmtCompact(v.market_cap)],
          ["FDV", fmtCompact(v.fdv)],
          ["Rank", v.market_cap_rank != null ? `#${v.market_cap_rank}` : DASH],
          ["Volume 24h", fmtCompact(v.volume_24h)],
          ["1y return", fmtPct(r["1y"])],
          ["Circulating", fmtCompact(s.circulating)],
          ["Total supply", fmtCompact(s.total)],
          ["Max supply", fmtCompact(s.max)],
          ["Open", fmtPx(v.ohlc?.open)],
          ["High", fmtPx(v.ohlc?.high)],
          ["Low", fmtPx(v.ohlc?.low)],
          ["Source", `${v.source ?? DASH} · ${v.data_quality ?? DASH}`],
        ].map(([k, val]) => (
          <div
            key={k as string}
            className="flex min-h-[1.875rem] items-center justify-between px-2 text-xs hover:bg-muted/20"
          >
            <span className="text-muted-foreground">{k}</span>
            <span className="font-number tabular-nums text-foreground">
              {val}
            </span>
          </div>
        ))}
      </div>
      <Prov p={q.provenance} />
    </div>
  );
}

export function AssetIntelligence({
  symbol,
  kind,
}: {
  symbol: string;
  kind: "metrics" | "signals" | "transfers";
}) {
  const [dataset, setDataset] = useState("metrics");
  const [nonce, setNonce] = useState(0);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        {kind === "metrics" ? (
          <select
            aria-label="Asset intelligence dataset"
            value={dataset}
            onChange={(e) => setDataset(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="metrics">Asset metrics</option>
            <option value="supply">Supply & emissions</option>
          </select>
        ) : (
          <h3 className="text-xs font-semibold">
            {kind === "signals"
              ? "Predictive track record"
              : "Transfers ≥ $250k"}
          </h3>
        )}
        <div className="flex items-center gap-2">
          {kind === "metrics" && <RunDiligenceButton asset={symbol} />}
          <button
            onClick={() => setNonce((n) => n + 1)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Refresh
          </button>
        </div>
      </div>
      <div key={symbol + kind + dataset + nonce}>
        {kind === "transfers" ? (
          <FlowTab sym={symbol} nonce={nonce} />
        ) : kind === "signals" ? (
          <PredictiveSignals sym={symbol} nonce={nonce} />
        ) : dataset === "supply" ? (
          <SupplyTab sym={symbol} nonce={nonce} />
        ) : (
          <StatsTab sym={symbol} nonce={nonce} />
        )}
      </div>
    </div>
  );
}

function PredictiveSignals({ sym, nonce }: { sym: string; nonce: number }) {
  const q = useProduct(
    `/v1/data/predictive-signals?assets=${encodeURIComponent(sym)}&_=${nonce}`,
  );
  if (q.error) return <Gate error={q.error} />;
  if (q.loading) return <Empty msg="Loading predictive signals…" />;
  const asset = q.value?.assets?.[0];
  if (!asset) return <Empty msg="No predictive coverage for this asset." />;
  const assetCard = asset.scorecard_asset ?? {};
  const globalCard = asset.scorecard_global ?? {};
  const horizonKeys = (card: any) =>
    Object.keys(card).filter(
      (key) => key.endsWith("h") && card[key] && typeof card[key] === "object",
    );
  const useAsset =
    horizonKeys(assetCard).length > 0 ||
    assetCard.status === "insufficient_sample";
  const card = useAsset ? assetCard : globalCard;
  const horizons = horizonKeys(card);
  return (
    <div className="space-y-3 text-xs">
      <p className="text-muted-foreground">
        {useAsset ? "Asset" : "Global"} track record ·{" "}
        {asset.active_calls?.length ?? 0} active calls
      </p>
      {horizons.length ? (
        horizons.map((h) => {
          const c = card[h];
          const sample = num(c.sample_size);
          const rate = num(c.hit_rate_90d);
          const lb = num(c.wilson_lb_90d);
          const enough =
            c.status !== "insufficient_sample" &&
            sample != null &&
            sample >= 20 &&
            rate != null;
          return (
            <div
              key={h}
              className="flex justify-between border-b border-border/50 py-2"
            >
              <span>{h} horizon</span>
              <span className="font-number tabular-nums">
                {enough
                  ? `${(rate * 100).toFixed(0)}% hit rate · n=${sample}${lb != null ? ` · at least ${(lb * 100).toFixed(0)}% (95%)` : ""}`
                  : `Insufficient history · n=${sample ?? 0}/20`}
              </span>
            </div>
          );
        })
      ) : (
        <p className="text-muted-foreground">
          Insufficient history · n={num(card.sample_size) ?? 0}/20 settled
          outcomes.
        </p>
      )}
      {(asset.active_calls ?? []).map((call: any, i: number) => (
        <div key={call.id ?? i} className="rounded border border-border p-2">
          <div className="flex justify-between">
            <strong>{String(call.direction).toUpperCase()}</strong>
            <span>{call.horizon_hours}h horizon</span>
          </div>
          <p className="mt-1 text-muted-foreground">
            Valid until{" "}
            {call.valid_until
              ? new Date(call.valid_until).toLocaleString()
              : "—"}
          </p>
        </div>
      ))}
      <Prov p={q.provenance} />
    </div>
  );
}

export function TransferFeed({
  onSelectAsset,
}: {
  onSelectAsset: (symbol: string) => void;
}) {
  const q = useProduct("/v1/data/whale-alerts?limit=8");
  const transfers: any[] = q.value?.transactions ?? [];
  return (
    <section className="strategy-panel">
      <div className="strategy-panel-head">
        <div>
          <h2>Capital flows</h2>
          <span>Transfers ≥ $250k</span>
        </div>
        <button
          onClick={q.refresh}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Refresh
        </button>
      </div>
      {q.error ? (
        <Gate error={q.error} />
      ) : q.loading ? (
        <Empty msg="Loading transfers…" />
      ) : !transfers.length ? (
        <Empty
          msg={q.value?.coverage ?? "No recent transfers in the tracked feed."}
        />
      ) : (
        <div className="divide-y divide-border/50">
          {transfers.map((t, i) => (
            <button
              key={`${t.chain ?? ""}-${t.tx_hash ?? i}`}
              onClick={() => onSelectAsset(t.asset)}
              disabled={!t.asset}
              className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/20"
            >
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-warning/60" />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2 text-xs">
                  <strong>{t.asset ?? "—"}</strong>
                  <span className="strategy-numeric">
                    ${fmtCompact(t.amount_usd)}
                  </span>
                </span>
                <span className="mt-1 block truncate text-xs text-muted-foreground">
                  {t.from_label ?? shortAddr(t.from)} →{" "}
                  {t.to_label ?? shortAddr(t.to)}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground/60">
                  {fmtAge(t.observed_at)} · {t.chain ?? "Tracked transfer"}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      <Prov p={q.provenance} />
    </section>
  );
}

// ── Contextual alerts ─────────────────────────────────────────────────────────
// One menu of presets next to an asset's readouts, posting to the same
// /v1/data/alert-rules engine as Market Signals → Alerts.

type AlertPreset = {
  label: string;
  metric: string;
  op: "gt" | "gte" | "lt" | "lte";
  value: number;
};

function alertPresets(price: number | null): AlertPreset[] {
  const out: AlertPreset[] = [];
  if (price != null && price > 0) {
    const up = Number((price * 1.05).toPrecision(4));
    const down = Number((price * 0.95).toPrecision(4));
    out.push(
      {
        label: `Price above ${fmtPx(up)} (+5%)`,
        metric: "price",
        op: "gt",
        value: up,
      },
      {
        label: `Price below ${fmtPx(down)} (−5%)`,
        metric: "price",
        op: "lt",
        value: down,
      },
    );
  }
  return [
    ...out,
    {
      label: "Up 5% or more in 24h",
      metric: "change_24h",
      op: "gte",
      value: 5,
    },
    {
      label: "Down 5% or more in 24h",
      metric: "change_24h",
      op: "lte",
      value: -5,
    },
    {
      label: "Funding at or above 0.01%/h",
      metric: "funding_rate",
      op: "gte",
      value: 0.0001,
    },
    {
      label: "Volume anomaly (z ≥ 3)",
      metric: "volume_zscore",
      op: "gte",
      value: 3,
    },
    { label: "RSI at or above 70", metric: "rsi_14", op: "gte", value: 70 },
    { label: "RSI at or below 30", metric: "rsi_14", op: "lte", value: 30 },
    {
      label: "A $1M+ transfer",
      metric: "whale_transfer_usd",
      op: "gte",
      value: 1_000_000,
    },
  ];
}

export function AlertPresets({
  symbol,
  price = null,
}: {
  symbol: string;
  price?: number | null;
}) {
  const { data: session, status } = useSession();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  if (status !== "authenticated") return null;
  const create = async (p: AlertPreset) => {
    setBusy(true);
    try {
      const res = await dpopApi.post(
        "/v1/data/alert-rules",
        {
          asset: symbol.toUpperCase(),
          conditions: [{ metric: p.metric, op: p.op, value: p.value }],
          channel: "push",
          channel_target: null,
          cooldown_minutes: 60,
        },
        session,
      );
      const body = await res.json().catch(() => null);
      if (!res.ok)
        throw new Error(
          typeof body?.detail === "string"
            ? body.detail
            : (body?.detail?.message ??
              `Couldn't create the alert (${res.status})`),
        );
      toast({
        title: `Alert set: ${symbol} · ${p.label}`,
        description: "You'll get a push when it fires.",
      });
    } catch (err) {
      toast({
        title: "Alert not created",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={busy}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-sm text-muted-foreground transition-colors duration-1 hover:bg-white/[0.04] hover:text-foreground disabled:opacity-50"
      >
        <BellPlus className="h-4 w-4" /> Alert
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Notify me about {symbol} when…
        </DropdownMenuLabel>
        {alertPresets(price).map((p) => (
          <DropdownMenuItem key={p.label} onSelect={() => create(p)}>
            {p.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Share the asset's One Link (the public /markets page). */
export function ShareAssetButton({ symbol }: { symbol: string }) {
  const upper = symbol.toUpperCase();
  return (
    <SharePopover
      target={{ kind: "market", ref: upper }}
      text={`${upper} on Fexr`}
      surface="asset_sheet"
      label="Share"
      showLabel
    />
  );
}

// ── Catalysts & deadlines ─────────────────────────────────────────────────────

const CATALYST_LABEL: Record<string, string> = {
  governance_deadline: "Vote closes",
  scheduled_unlock: "Scheduled unlock",
  fundraising: "Raise",
  tracked_trader_move: "Tracked trader",
  signal_call_deadline: "Call settles",
  security_incident: "Hack",
};

export function CatalystsPanel({ symbol }: { symbol: string }) {
  const q = useProduct(
    `/v1/data/catalysts?asset=${encodeURIComponent(symbol)}`,
  );
  if (q.error) return <Gate error={q.error} />;
  if (q.loading && !q.value) return <Empty msg="Loading catalysts…" />;
  const events: any[] = q.value?.events ?? [];
  const upcoming = events.filter((e) => e.upcoming);
  const recent = events.filter((e) => !e.upcoming).reverse();
  const Row = ({ e }: { e: any }) => (
    <li className="flex items-start justify-between gap-3 py-2 text-xs">
      <span className="min-w-0">
        <span className="text-muted-foreground">
          {CATALYST_LABEL[e.type] ?? e.type}
        </span>{" "}
        {e.url ? (
          <a
            href={e.url}
            target="_blank"
            rel="noreferrer"
            className="text-foreground hover:underline"
          >
            {e.title ?? DASH}
          </a>
        ) : e.type === "tracked_trader_move" && e.detail?.story_id ? (
          <Link
            href={`/w/${e.detail.story_id}`}
            className="text-foreground hover:underline"
          >
            {e.title ?? DASH}
          </Link>
        ) : (
          <span className="text-foreground">{e.title ?? DASH}</span>
        )}
      </span>
      <span className="shrink-0 font-number tabular-nums text-muted-foreground">
        {e.ts ? new Date(e.ts).toLocaleDateString() : DASH}
      </span>
    </li>
  );
  return (
    <div className="space-y-3">
      {events.length === 0 ? (
        <Empty msg={`No dated catalysts for ${symbol} in the next 30 days.`} />
      ) : (
        <>
          {upcoming.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground">
                Coming up
              </h4>
              <ul className="divide-y divide-border/50">
                {upcoming.map((e, i) => (
                  <Row key={`u-${i}`} e={e} />
                ))}
              </ul>
            </div>
          )}
          {recent.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground">
                Recently
              </h4>
              <ul className="divide-y divide-border/50">
                {recent.slice(0, 8).map((e, i) => (
                  <Row key={`r-${i}`} e={e} />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <p className="text-xs text-muted-foreground">
        Not an exhaustive calendar: unlocks only where a dated schedule exists,
        and past raises are context, not catalysts. Hacks are DeFiLlama&apos;s
        estimates, shown only when the hacked protocol has this token.
      </p>
      <Prov p={q.provenance} />
    </div>
  );
}

// ── Backers & raises ──────────────────────────────────────────────────────────

/**
 * Who funded the project behind an asset. Matched on the project's exact name,
 * so an asset without a name (or a name the source spells differently) shows
 * nothing rather than another company's rounds.
 */
export function RaisesPanel({ name }: { name: string }) {
  const q = useProduct(
    `/v1/data/fundraising?protocol=${encodeURIComponent(name)}&exact=true&limit=20`,
  );
  if (q.error) return <Gate error={q.error} />;
  if (q.loading && !q.value) return <Empty msg="Loading raises…" />;
  const rounds: any[] = q.value?.rounds ?? [];
  if (rounds.length === 0)
    return <Empty msg={`No announced rounds found for ${name}.`} />;
  const total = rounds.reduce((a, r) => a + (num(r.amount_usd) ?? 0), 0);
  const backers = Array.from(
    new Set(rounds.flatMap((r) => (r.investors ?? []) as string[])),
  );
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {rounds.length} round{rounds.length === 1 ? "" : "s"}
        {total > 0 ? ` · $${fmtCompact(total)} disclosed` : ""}
        {backers.length > 0
          ? ` · ${backers.length} backer${backers.length === 1 ? "" : "s"}`
          : ""}
      </p>
      <ul className="divide-y divide-border/50">
        {rounds.map((r, i) => (
          <li key={i} className="py-2 text-xs">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">
                {r.source ? (
                  <a
                    href={r.source}
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground hover:underline"
                  >
                    {r.round || "Round"}
                  </a>
                ) : (
                  <span className="text-foreground">{r.round || "Round"}</span>
                )}
                {num(r.valuation_usd) ? (
                  <span className="text-muted-foreground">
                    {" "}
                    at ${fmtCompact(r.valuation_usd)}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 font-number tabular-nums">
                {num(r.amount_usd)
                  ? `$${fmtCompact(r.amount_usd)}`
                  : "Undisclosed"}
                <span className="ml-2 text-muted-foreground">
                  {r.date ? new Date(r.date).toLocaleDateString() : DASH}
                </span>
              </span>
            </div>
            {r.investors?.length ? (
              <p className="mt-0.5 text-muted-foreground">
                {r.investors.join(", ")}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">
        Announced rounds only. Lead investors aren&apos;t marked because the
        source names participants without saying who led.
      </p>
      <Prov p={q.provenance} />
    </div>
  );
}

/**
 * The signed-in layers of the public asset page (/markets/[asset]). Supply,
 * transfers, the predictive record and catalysts are Fexr data products;
 * signed-out visitors see a sign-in prompt, never placeholder numbers.
 */
export function PublicAssetLayers({
  symbol,
  name,
}: {
  symbol: string;
  name?: string | null;
}) {
  const { status } = useSession();
  const loginHref = useLoginRedirectHref();
  if (status === "loading") return null;
  if (status !== "authenticated") {
    return (
      <div className="glass-card rounded-lg border bg-card p-4">
        <p className="text-sm font-medium">More on {symbol} in Fexr</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Supply and emissions, transfers of $250k or more, the predictive track
          record, upcoming catalysts and who funded it.
        </p>
        <Link
          href={loginHref}
          className="mt-3 inline-flex text-sm text-primary hover:underline"
        >
          Sign in to see them
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <section className="glass-card rounded-lg border bg-card p-4">
        <AssetIntelligence symbol={symbol} kind="metrics" />
      </section>
      <section className="glass-card rounded-lg border bg-card p-4">
        <h3 className="mb-3 text-xs font-semibold">Catalysts & deadlines</h3>
        <CatalystsPanel symbol={symbol} />
      </section>
      {name ? (
        <section className="glass-card rounded-lg border bg-card p-4">
          <h3 className="mb-3 text-xs font-semibold">Backers & raises</h3>
          <RaisesPanel name={name} />
        </section>
      ) : null}
      <section className="glass-card rounded-lg border bg-card p-4">
        <AssetIntelligence symbol={symbol} kind="signals" />
      </section>
      <section className="glass-card rounded-lg border bg-card p-4">
        <AssetIntelligence symbol={symbol} kind="transfers" />
      </section>
    </div>
  );
}
